import {
    createAsyncThunk,
    createSlice,
    current,
    PayloadAction,
} from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import notifySuccess from "../../utils/Notifications/Success";
import notifyError from "../../utils/Notifications/Error";
import { applyTicker, getTickersByGroup } from "./GroupsSlice";
import { RootState } from "../store";
import { setPendingCommand } from "store/reducers/LogsSlice";
import { TradeParamsRequest } from "utils/trade-params-handler";
import Cookies from "js-cookie";

import { getTickers } from "./TabsSlice";

export type SettingsParamType = "String" | "Boolean" | "Float" | "Integer";
export type ParamGroupType = "CP" | "SP" | "MDP";
export interface SavedSettingsItemAffiliation {
    group_name: string;
    ticker_name: string;
    exchange_name: string;
}

export interface SavedSattingsItemAbstract {
    key: string;
    value: any;
    type?: SettingsParamType;
    description?: string | null;
    has_change?: boolean;
    source?: string;
}
export interface TickerBlockItem {
    group_name: string;
    ticker_name: string;
    blocked_by: string;
    blocked_at: string;
}

export interface TickerBlocksState {
    items: TickerBlockItem[];
    loading: boolean;
    lastUpdate: string | null;
}

export interface SavedSettingsItem extends SavedSattingsItemAbstract {
    affiliation: SavedSettingsItemAffiliation;
}

export type SavedCPSettingsState = {
    [id: string]: SavedSettingsItem[];
};

export type SavedSettingsState = SavedSettingsItem[];
export type SavedSettingsDefaults = {
    [key in ParamGroupType]: SavedSettingsItem[];
};

export interface WorkspaceTab {
    id: number;
    name: string;
    is_active: boolean;
    configs_readonly: Array<{
        config_id: number;
    }>;
}
export interface TickerConfig {
    config_code: "CP" | "MDP" | "SP" | "IDPOS" | "SEC" | "MOPT";
    id: number;
    values: string[][];
    all_values: string[][];
    is_horizontal?: boolean;
}

export interface ConfigFileItem {
    id: number;
    name: string; // "CP" | "SP" | "MDP" | "IDPOS" | "SEC" | "MOPT"
    description: string | null;
    readonly: boolean;
    is_robot_stopped: boolean;
    include_in_apply: boolean;
    is_uniq_ui: boolean;
    created_at: string;
    updated_at: string;
}

export type ConfigFieldType = "Integer" | "Float" | "String" | "Boolean";
export type autoFillType = {
    source_file: string;
    source_field: string;
};
export interface ConfigFileField {
    autofill: autoFillType[];
    id: number;
    name: string;
    field_type: ConfigFieldType;
    default_value: string | null;
    description: string;
    is_editable: boolean;
    is_required: boolean;
    is_unique: boolean;
    order: number;
}

export interface ConfigParamMetadata {
    types: Record<string, SettingsParamType>;
    defaultValues: Record<string, string | null>;
    descriptions: Record<string, string | null>;
}
export interface ConfigFileDetails extends ConfigFileItem {
    fields: ConfigFileField[];
}

type SettingsState = {
    saved_CP_settings: any;
    tickerBlocks: TickerBlocksState;
    openedMDPRows: Record<number, string | null>;
    saved_settings: SavedSettingsState;
    loading: boolean;
    error: null | string | { block: string };
    isReset: boolean;
    resetKey: number;
    searchString: string;
    workspaceTabs: WorkspaceTab[];
    activeTabId: number | null;

    MDPDefaultValues: Record<string, string | null>;
    MDPTypes: Array<string>;
    MDPParamTypes: Record<string, string>;
    MDPSelectedItem: { SPname: string };
    activeGroup: { name: string; id?: number };
    activeTicker: {
        name: string;
        isNew: boolean;
        index: number;
        schedule_id: string;
        hedgeinst_id: string;
    };

    isChanged: boolean;
    isNewTickerAdding: boolean;
    configs: TickerConfig[];
    saved_configs: TickerConfig[];
    resetConfigsCounter: number;
    changedCPColumns: string[];
    changedMDPColumns: string[];
    isTypeError: boolean;
    availableSCIds: String[];
    availableSTSIds: String[];
    selectedSCId: null | string;
    selectedSTSId: { id: number; value: null | string }[];
    scSchedules: { id: string; start_time: string; end_time: string }[] | null;
    stsSchedules: { id: string; start_time: string; end_time: string }[] | null;
    sheduleExists: string[] | null;
    availableTickers:
        | { index: number; ticker_name: string; schedule_id: string }[]
        | null;
    mdpSources: string[] | null;
    mdpOpenStates: Array<{
        config_id: number;
        open_rows: string[];
        group_name?: string;
        ticker_name?: string;
        updated_at?: string;
    }> | null;

    configParams: Record<string, ConfigParamMetadata>;
    showDescriptions: boolean;
    configFilesList: ConfigFileItem[];
    configFilesDetails: Record<string, ConfigFileDetails>;
    configFilesLoading: boolean;
    configFilesError: string | null;
    editMode: boolean;
    validationErrors: Record<string, boolean>;
    workspaceDrafts: Record<number, TickerConfig[]>;
    workspaceSnapshots: Record<number, TickerConfig[]>;
    workspaceDirty: Record<number, boolean>;
    workspaceValidationErrors: Record<number, Record<string, boolean>>;
    sharedConfigValues: Record<string, Record<string, string>>;
    sharedTableValues: Record<string, string[][]>;
    tickerDraftDirty: boolean;
    applyFiles: { id: number | string; name: string }[];
};

const cloneConfigs = (configs: TickerConfig[] = []): TickerConfig[] => {
    const cloned = configs.map((config) => ({
        ...config,
        values: config.values?.map((row) => [...row]) || [],
        all_values: config.all_values?.map((row) => [...row]) || [],
    }));

    const mdpConfigs = cloned.filter(
        (config) => config.config_code === "MDP" && config.all_values?.length,
    );
    if (mdpConfigs.length < 2) return cloned;

    type MergedMDPRow = {
        values: Record<string, string | null>;
        isTemporary: boolean;
    };
    const mergedRows = new Map<string, MergedMDPRow>();

    mdpConfigs.forEach((config) => {
        const header = config.all_values[0] || [];
        // Rows with the same schedule/source/type can still be distinct rows
        // (for example, separate ibd rows with different parameter values).
        // Merge matching occurrences across configs, but preserve duplicates
        // within each config by adding their occurrence number to the key.
        const rowOccurrences = new Map<string, number>();
        config.all_values.slice(1).forEach((row) => {
            const valueByField: Record<string, string | null> = {};
            header.forEach((fieldName, index) => {
                valueByField[fieldName] = row[index] ?? null;
            });

            const scheduleField = header.includes("SheduleID")
                ? "SheduleID"
                : "ScheduleID";
            const rowKey = [
                valueByField[scheduleField],
                valueByField["ID.depend"],
                valueByField["ID.source"],
                valueByField["Source.Type"],
                valueByField["Source.type.shedule.ID"],
            ].join("\u001f");

            const occurrence = rowOccurrences.get(rowKey) || 0;
            rowOccurrences.set(rowKey, occurrence + 1);
            const mergeKey = `${rowKey}\u001e${occurrence}`;
            const merged = mergedRows.get(mergeKey) || {
                values: {},
                isTemporary: false,
            };
            Object.entries(valueByField).forEach(([fieldName, value]) => {
                const currentValue = merged.values[fieldName];
                if (
                    (currentValue === null ||
                        currentValue === undefined ||
                        currentValue === "") &&
                    value !== null &&
                    value !== ""
                ) {
                    merged.values[fieldName] = value;
                } else if (!(fieldName in merged.values)) {
                    merged.values[fieldName] = value;
                }
            });
            merged.isTemporary ||= row[row.length - 1] === "new_temp_flag";
            mergedRows.set(mergeKey, merged);
        });
    });

    mdpConfigs.forEach((config) => {
        const header = config.all_values[0] || [];
        config.all_values = [
            [...header],
            ...Array.from(mergedRows.values()).map((merged) => {
                const row = header.map(
                    (fieldName) => merged.values[fieldName] ?? null,
                );
                if (merged.isTemporary) row.push("new_temp_flag");
                return row;
            }),
        ];
    });

    return cloned;
};

const applySharedValues = (
    state: SettingsState,
    configs: TickerConfig[] = [],
): TickerConfig[] =>
    cloneConfigs(configs).map((config) => {
        const sharedTable = state.sharedTableValues[config.config_code];
        if (sharedTable) {
            config.all_values = sharedTable.map((row) => [...row]);
            return config;
        }

        const sharedValues = state.sharedConfigValues[config.config_code];
        if (!sharedValues || !config.values?.[0]) return config;
        config.values[1] = config.values[0].map((key, index) =>
            Object.prototype.hasOwnProperty.call(sharedValues, key)
                ? sharedValues[key]
                : config.values[1]?.[index] || "",
        );
        return config;
    });

const cacheActiveWorkspaceDraft = (state: SettingsState) => {
    if (state.activeTabId === null) return;
    const hasLoadedWorkspace =
        state.workspaceSnapshots[state.activeTabId] !== undefined ||
        state.workspaceDirty[state.activeTabId] ||
        state.saved_configs.length > 0;
    if (!hasLoadedWorkspace) return;
    state.workspaceDrafts[state.activeTabId] = cloneConfigs(
        state.saved_configs,
    );
};

const initialState: SettingsState = {
    tickerBlocks: {
        items: [],
        loading: false,
        lastUpdate: null,
    },
    workspaceTabs: [],
    openedMDPRows: {},
    activeTabId: null,
    saved_settings: [],
    loading: false,
    error: null,
    isReset: true,
    resetKey: 0,
    searchString: "",
    isChanged: false,
    isNewTickerAdding: false,
    MDPSelectedItem: { SPname: "" },
    activeGroup: { name: "Группа", id: -1 },
    activeTicker: {
        name: "Название тикера",
        isNew: false,
        schedule_id: "",
        index: -1,
        hedgeinst_id: "",
    },
    MDPTypes: [],
    MDPDefaultValues: {},

    MDPParamTypes: {},

    configs: [],
    saved_configs: [],
    saved_CP_settings: {},
    resetConfigsCounter: 0,
    changedCPColumns: [],
    changedMDPColumns: [],

    isTypeError: false,
    availableSCIds: [],
    availableSTSIds: [],
    selectedSCId: null,
    selectedSTSId: [],
    sheduleExists: null,
    mdpSources: null,
    scSchedules: null,
    stsSchedules: null,
    availableTickers: null,
    mdpOpenStates: null,

    showDescriptions: false,
    configParams: {},
    configFilesList: [],
    configFilesDetails: {},
    configFilesLoading: false,
    configFilesError: null,
    editMode: false,
    validationErrors: {},
    workspaceDrafts: {},
    workspaceSnapshots: {},
    workspaceDirty: {},
    workspaceValidationErrors: {},
    sharedConfigValues: {},
    sharedTableValues: {},
    tickerDraftDirty: false,
    applyFiles: [],
};

export const getMDP = createAsyncThunk("get/mdp", async (_, { dispatch }) => {
    try {
        const response = await instance.get(`${pathname}/api/mdp-parameters`);
        return response.data;
    } catch (e) {
        notifyError(
            Object.values(e.response?.data || {})
                .flat()
                .join(" ") ||
                e.message ||
                "Произошла ошибка",
        );
    }
});

export const downloadLogsFile = createAsyncThunk(
    "get/logs-file",
    async (
        { link }: { link: string },
        { rejectWithValue, dispatch, getState },
    ) => {
        let auth_header = "";
        if (Cookies.get("access")) {
            auth_header = "Bearer " + Cookies.get("access");
        }
        const response = await fetch(
            `${pathname}${link.replace("media", "api")}`,
            {
                headers: {
                    Authorization: auth_header,
                },
            },
        );
        const file_name = link.split("/")[link.split("/").length - 1];

        if (!response.ok) {
            notifyError(`Файл ${file_name} пустой`);
        }
        const split = link.split("/");
        const file = split[split.length - 1];
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const file_link = document.createElement("a");
        file_link.href = url;
        file_link.setAttribute("download", file);
        document.body.appendChild(file_link);
        file_link.click();
        file_link.remove();
        return 1;
    },
);

export const updateTickerSettings = createAsyncThunk(
    "post/ticker",
    async (
        { body, notify }: { body: TradeParamsRequest; notify?: boolean },
        { rejectWithValue, dispatch },
    ) => {
        let response;
        try {
            response = await instance.post(pathname + "/api/ticker", body);
            notifySuccess("Настройки тикера отправлены");
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Произошла ошибка",
            );
            dispatch(getTickerSettings());
        }
        if (notify !== false) {
            dispatch(applyTicker({ tickerName: null }));
        }
        dispatch(getTickerSettings());
        return response.data;
    },
);

export const searchTickers = createAsyncThunk(
    "get/search_tickers",
    async (
        { group_name, query }: { group_name: string; query: string },
        { rejectWithValue, dispatch },
    ) => {
        let response;
        try {
            response = await instance.get(
                pathname +
                    `/api/ticker/search?group_name=${group_name}&query=${query}
`,
            );
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.response?.data);
            }
        }

        return response?.data;
    },
);

export const getScheduleParams = createAsyncThunk(
    "get/scheduleParams",
    async (
        { group, ticker }: { group?: string; ticker?: string } = {},
        { rejectWithValue, dispatch },
    ) => {
        let response;
        try {
            const params = {
                ...(group ? { group } : {}),
                ...(ticker ? { ticker } : {}),
            };
            response = await instance.get(
                pathname + `/api/schedule-parameters`,
                { params },
            );
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
        }

        if (response.data) {
            dispatch(setAvailableSCParams(response.data["sc_schedule_ids"]));
            dispatch(setAvailableSTSParams(response.data["sts_schedule_ids"]));
        }

        return response?.data;
    },
);

export const getScheduleIdTickers = createAsyncThunk(
    "get/scheduleIdTickers",
    async (
        { group_name, scheduleId }: { group_name: string; scheduleId: string },
        { rejectWithValue, dispatch },
    ) => {
        let response;

        dispatch(setAvailableTickers(null));
        try {
            response = await instance.get(
                pathname +
                    `/api/schedule-tickers?schedule_id=${scheduleId}&group_name=${group_name}`,
            );
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
        }

        if (response.data) dispatch(setAvailableTickers(response.data));
        return response?.data;
    },
);

export const getMDPSourceTypes = createAsyncThunk(
    "get/MDPSourceTypes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `/api/source-parameter`,
            );
            // isEqual(response.data)
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
        }
    },
);

export const getTickerSettings = createAsyncThunk(
    "get/tickerSettings",
    async (_, { rejectWithValue, getState, dispatch }) => {
        const { settings } = getState() as any;

        const {
            activeTicker: ticker,
            activeGroup: group,
            activeTabId,
        } = settings;
        const groupName = group.name;
        if (group.name === "Группа")
            return rejectWithValue("Группа не выбрана");
        try {
            const response = await instance.get(
                pathname +
                    `/api/ticker/${ticker.name}?group_name=${groupName}&workspace_id=${activeTabId}&schedule_id=${ticker.schedule_id}&index=${ticker.index !== -1 ? ticker.index : ""}`,
            );
            // блокируем как только нас пустили для редактирования
            dispatch(
                changeTickerBlockStatus({
                    blockIt: true,
                    ticker: ticker.name,
                    group: group.name,
                }),
            );
            dispatch(
                dropState({
                    exclude_ticker: ticker.name,
                }),
            );

            return { configs: response.data, workspaceId: activeTabId };
        } catch (e) {
            // @ts-ignore
            if (e.response.status === 409) {
                notifyError("Тикер занят другим пользователем");
            }
            if (axios.isAxiosError(e)) {
                const messages = Object.values(e.response.data)
                    .flat()
                    .join(", ");
                notifyError(messages);

                return rejectWithValue(e.response?.data);
            }
        }
    },
    {
        condition: (_, { getState }) => {
            const { activeTicker } = (getState() as any).settings;
            return (
                !!activeTicker.name && activeTicker.name !== "Название тикера"
            );
        },
    },
);

export const changeTickerBlockStatus = createAsyncThunk(
    "change/TickerBlockStatus",
    async (
        {
            blockIt,
            ticker,
            group,
        }: { blockIt: boolean; ticker: string; group: string },
        { rejectWithValue },
    ) => {
        const body = { group_name: group, ticker_name: ticker };
        const reqType = blockIt ? "block" : "unblock";
        try {
            const response = await instance.post(
                pathname + `/api/${reqType}`,
                body,
            );
            return response.data;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Произошла ошибка",
            );
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const getTickerWorkspaces = createAsyncThunk(
    "get/ticker-workspaces",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                `${pathname}/api/ticker-workspace`,
            );
            return response.data;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Произошла ошибка",
            );
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
            return rejectWithValue(
                "Неизвестная ошибка при загрузке рабочих областей",
            );
        }
    },
);

export const createTickerWorkspace = createAsyncThunk(
    "post/ticker-workspace",
    async (
        body: {
            name: string;
            is_active: boolean;
            configs: { config_id: number; position: number }[];
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await instance.post(
                `${pathname}/api/ticker-workspace`,
                body,
            );
            notifySuccess("Рабочая область успешно создана");
            return response.data;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Ошибка при создании рабочей области",
            );
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
            return rejectWithValue("Неизвестная ошибка");
        }
    },
);

export const updateTickerWorkspace = createAsyncThunk(
    "patch/ticker-workspace",
    async (
        {
            id,
            body,
        }: {
            id: number;
            body: {
                name?: string;
                is_active?: boolean;
                configs?: { config_id: number; position: number }[];
            };
        },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const response = await instance.patch(
                `${pathname}/api/ticker-workspace/${id}`,
                body,
            );
            notifySuccess("Рабочая область успешно обновлена");

            const state = getState() as RootState;
            const isTickerSelected =
                !!state.settings.activeTicker.name &&
                state.settings.activeTicker.name !== "Название тикера";
            if (isTickerSelected && !state.settings.workspaceDrafts[id]) {
                dispatch(getTickerSettings());
            }

            return response.data;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Ошибка при обновлении рабочей области",
            );
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
            return rejectWithValue("Неизвестная ошибка");
        }
    },
);
export const deleteMDPRowAndSave = createAsyncThunk(
    "settings/deleteMDPRowAndSave",
    async (
        { configId, rowIndex }: { configId: number; rowIndex: number },
        { dispatch, getState },
    ) => {
        // 1. Удаляем локально
        dispatch(removeMDPRow({ configId, rowIndex }));

        // 2. Сохраняем всё
        const result = await dispatch(saveTickerSettingsNew({ notify: true,dontApply: true }));
        return result;
    },
);
export const saveTickerSettingsNew = createAsyncThunk(
    "post/ticker-new",
    async (
        {
            notify = false,
            for_all = false,
            config_ids,
            dontApply = false,
        }: {
            notify?: boolean;
            for_all?: boolean;
            dontApply?: boolean;
            config_ids?: (number | string)[];
        },
        { getState, dispatch, rejectWithValue },
    ) => {
        const state = getState() as RootState;
        const { activeTicker, activeGroup, saved_configs, configFilesDetails } =
            state.settings;

        const tickerName = activeTicker.name;
        const groupName = activeGroup.name;

        const activeTab = state.settings.workspaceTabs.find(
            (tab) => tab.id === state.settings.activeTabId,
        );
        const displayedConfigIds = new Set<number>(
            activeTab?.configs_readonly?.map((c) => c.config_id) || [],
        );

        const validateRequiredFields = (): boolean => {
            for (const config of saved_configs) {
                const metadata = configFilesDetails[config.config_code];
                if (!metadata || !displayedConfigIds.has(config.id)) continue;

                const requiredFields = metadata.fields.filter(
                    (f) => f.is_required,
                );
                if (requiredFields.length === 0) continue;

                const isUniq = metadata.is_uniq_ui;

                if (isUniq) {
                    if (
                        !Array.isArray(config.all_values) ||
                        config.all_values.length < 1
                    )
                        continue;
                    const header = config.all_values[0] || [];
                    const requiredNames = requiredFields.map((f) => f.name);
                    const rows = config.all_values.slice(1);

                    for (let r = 0; r < rows.length; r++) {
                        const row = rows[r];
                        if (row[row.length - 1] === "new_temp_flag") continue;

                        for (const fieldName of requiredNames) {
                            const idx = header.indexOf(fieldName);
                            if (idx !== -1 && !row[idx]?.trim()) {
                                notifyError(
                                    `Поле "${fieldName}" обязательно для заполнения`,
                                );
                                dispatch(
                                    setConfigValidationError({
                                        configId: config.id,
                                        fieldKey: `required:${r}:${fieldName}`,
                                        hasError: true,
                                    }),
                                );
                                return false;
                            }
                        }
                    }
                } else if (!isUniq) {
                    if (
                        !Array.isArray(config.values) ||
                        config.values.length < 2
                    )
                        continue;
                    const [keys, values] = config.values || [[], []];
                    for (const field of requiredFields) {
                        const idx = keys.indexOf(field.name);
                        if (idx === -1) continue;
                        if (!values[idx]?.trim()) {
                            notifyError(
                                `Поле "${field.name}" обязательно для заполнения`,
                            );
                            dispatch(
                                setConfigValidationError({
                                    configId: config.id,
                                    fieldKey: `required:${field.name}`,
                                    hasError: true,
                                }),
                            );
                            return false;
                        }
                    }
                }
            }
            return true;
        };

        if (!validateRequiredFields()) {
            return rejectWithValue({ error: "validation_failed" });
        }

        const nonUniqGroups: Record<string, TickerConfig[]> = {};
        const uniqConfigs: TickerConfig[] = [];

        saved_configs.forEach((config) => {
            const metadata = configFilesDetails[config.config_code];
            if (!metadata) return;

            if (metadata.is_uniq_ui) {
                uniqConfigs.push(config);
                return;
            }

            const hasValues = config.values?.[0]?.length > 0;
            const isInCurrentTab = displayedConfigIds.has(config.id);

            if (!isInCurrentTab && !hasValues) {
                return;
            }

            const code = config.config_code;
            if (!nonUniqGroups[code]) nonUniqGroups[code] = [];
            nonUniqGroups[code].push(config);
        });

        const mergeKeyValueConfigs = (
            configs: TickerConfig[],
        ): [string[], string[]] | undefined => {
            if (configs.length === 0) return undefined;

            const keyToValue = new Map<string, string>();

            configs.forEach((config) => {
                const keys = config.values?.[0] || [];
                const values = config.values?.[1] || [];

                keys.forEach((key, idx) => {
                    const trimmed = key.trim();
                    if (!trimmed) return;

                    const val = values[idx] || "";
                    if (val !== "" || !keyToValue.has(trimmed)) {
                        keyToValue.set(trimmed, val);
                    }
                });
            });

            if (keyToValue.size === 0) return undefined;

            const finalKeys: string[] = [];
            const finalVals: string[] = [];
            keyToValue.forEach((v, k) => {
                finalKeys.push(k);
                finalVals.push(v);
            });

            return [finalKeys, finalVals];
        };

        const body: Record<string, any> = {
            group_name: groupName,
            ticker_name: tickerName,
        };

        if (activeTicker.schedule_id && activeTicker.index !== -1) {
            body.schedule_id = activeTicker.schedule_id;
            body.index = activeTicker.index !== -1 ? activeTicker.index : "";
        }

        Object.entries(nonUniqGroups).forEach(([configCode, configs]) => {
            const merged = mergeKeyValueConfigs(configs);
            if (merged) {
                body[configCode] = merged;
            }
        });

        Object.entries(state.settings.sharedConfigValues).forEach(
            ([configCode, sharedValues]) => {
                const current = body[configCode] || [[], []];
                const valueByKey = new Map<string, string>();
                (current[0] || []).forEach((key: string, index: number) => {
                    valueByKey.set(key, current[1]?.[index] || "");
                });
                Object.entries(sharedValues).forEach(([key, value]) => {
                    valueByKey.set(key, value);
                });
                body[configCode] = [
                    [...valueByKey.keys()],
                    [...valueByKey.values()],
                ];
            },
        );

        uniqConfigs.forEach((config) => {
            const metadata = configFilesDetails[config.config_code];
            if (!metadata || !config.all_values) return;

            const configCode = config.config_code;
            const tableData: string[][] = [];
            const header = config.all_values[0];
            if (header?.length > 0) tableData.push(header);

            config.all_values.slice(1).forEach((row) => {
                if (!row.some((c) => c !== "")) return;
                const cleanedRow = [...row];
                if (cleanedRow[cleanedRow.length - 1] === "new_temp_flag") {
                    cleanedRow.pop();
                }
                tableData.push(cleanedRow);
            });

            if (tableData.length > 1) {
                body[configCode] = tableData;
            }
        });

        Object.entries(state.settings.sharedTableValues).forEach(
            ([configCode, table]) => {
                body[configCode] = table
                    .filter(
                        (row, index) =>
                            index === 0 || row.some((cell) => cell !== ""),
                    )
                    .map((row) =>
                        row[row.length - 1] === "new_temp_flag"
                            ? row.slice(0, -1)
                            : [...row],
                    );
            },
        );

        try {
            const response = await instance.post(
                `${pathname}/api/ticker`,
                body,
            );
            dispatch(getTickers());
            dispatch(getTickersByGroup(groupName));
            if (response.data.hasOwnProperty("Предупреждение")) {
                notifyError(response.data["Предупреждение"]);
                return rejectWithValue({
                    warning: response.data["Предупреждение"],
                });
            }

            if (
                response.data.index !== undefined &&
                response.data.schedule_id
            ) {
                dispatch(
                    setSelectedTicker({
                        name: tickerName,
                        schedule_id: response.data.schedule_id,
                        index: response.data.index,
                    }),
                );
            }

            notifySuccess("Настройки тикера отправлены");
            const isDefaultGroup =
                activeGroup.name === "default" ||
                (Array.isArray(activeGroup.name) &&
                    activeGroup.name[1] === true);
            if (notify && !dontApply) {
                const applyResult = await dispatch(
                    applyTicker({
                        tickerName: null,
                        config_ids,
                        bodyObject: {
                            for_all,
                            ...(for_all
                                ? {
                                      cp_columns:
                                          state.settings.changedCPColumns,
                                      mdp_columns:
                                          state.settings.changedMDPColumns,
                                  }
                                : {}),
                        },
                        skipSocket: isDefaultGroup,
                    }),
                );
                if (applyTicker.fulfilled.match(applyResult)) {
                    dispatch(resetChangedColumns());
                }
            }

            dispatch(getTickerSettings());
            return response.data;
        } catch (err: any) {
            notifyError(
                `Ошибка сохранения: ${err.response?.data?.detail?.[0] || err.response.data[0] || err.response?.status}`,
            );
            return rejectWithValue(err.response?.data);
        }
    },
);
export const addColumnConfig = createAsyncThunk(
    "post/column-config",
    async (
        {
            groupId,
            config_code,
            nameTicker,
            workspace_id,
            is_horizontal,
        }: {
            groupId: string | string[];
            config_code: string;
            nameTicker: string;
            workspace_id: number;
            is_horizontal?: boolean;
        },
        { rejectWithValue, dispatch },
    ) => {
        const groupName = groupId;
        const body = {
            config_code,
            columns: [],
            is_horizontal: is_horizontal,
            workspace_id,
        };

        try {
            const response = await instance.post(
                `${pathname}/api/ticker/${nameTicker}/column-config?group_name=${groupName}`,
                body,
            );
            notifySuccess(
                `Конфиг ${config_code} для ${nameTicker} успешно создан`,
            );

            dispatch(getTickerSettings());

            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }

            if (e.response.status === 409) {
                notifyError("Тикер занят другим пользователем");
            } else {
                notifyError(
                    `Ошибка при создании конфига ${config_code} для ${nameTicker}`,
                );
            }
            return rejectWithValue("Ошибка добавления конфига");
        }
    },
);

export const updateColumnConfig = createAsyncThunk(
    "patch/column-config",
    async (
        {
            nameTicker,
            configId,
            config_code,
            groupName,
            is_horisontal,
            isEdited,
            columns,
        }: {
            nameTicker: string;
            configId: number;
            config_code: string;
            groupName: string;
            is_horisontal?: boolean;
            isEdited?: boolean;
            columns: string[];
        },
        { rejectWithValue },
    ) => {
        const body = {
            config_code,
            columns,
            is_horisontal,
        };

        try {
            const response = await instance.patch(
                `${pathname}/api/ticker/${nameTicker}/column-config/${configId}?group_name=${groupName}`,
                body,
            );
            notifySuccess(
                `Конфиг ${config_code} ${isEdited ? "изменен" : "добавлен"}`,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.response?.data);
            }
            if (e.response.status === 409) {
                notifyError("Тикер занят другим пользователем");
            }
            return rejectWithValue("Ошибка обновления конфига");
        }
    },
);
export const deleteTickerWorkspace = createAsyncThunk(
    "delete/ticker-workspace",
    async (id: number, { rejectWithValue }) => {
        try {
            await instance.delete(`${pathname}/api/ticker-workspace/${id}`);
            notifySuccess("Рабочая область успешно удалена");
            return id;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Ошибка при удалении рабочей области",
            );
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data);
            }
            return rejectWithValue("Неизвестная ошибка");
        }
    },
);
export const deleteTickerWorkspaceConfig = createAsyncThunk(
    "delete/ticker-workspace-config",
    async (
        { workspaceId, configId }: { workspaceId: number; configId: number },
        { rejectWithValue, dispatch },
    ) => {
        try {
            await instance.delete(
                `${pathname}/api/ticker-workspace/${workspaceId}/config/${configId}`,
            );

            notifySuccess("Конфиг успешно удалён из рабочей области");

            dispatch(getTickerSettings());

            return { workspaceId, configId };
        } catch (e: any) {
            if (axios.isAxiosError(e)) {
                if (e.response?.status === 409) {
                    notifyError("Тикер занят другим пользователем");
                }
                return rejectWithValue(e.response?.data || e.message);
            }
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Произошла ошибка",
            );
            return rejectWithValue("Неизвестная ошибка");
        }
    },
);
export const getMDPOpenState = createAsyncThunk(
    "settings/getMDPOpenState",
    async (
        {
            group_name,
            ticker_name,
            config_id,
            schedule_id,
        }: {
            group_name: string;
            ticker_name: string;
            config_id: number;
            schedule_id: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await instance.get(
                `${pathname}/api/mdp-open-state`,
                { params: { group_name, ticker_name, config_id, schedule_id } },
            );
            return { config_id, data: response.data };
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Ошибка получения open state",
            );
        }
    },
);

export const setMDPOpenState = createAsyncThunk(
    "settings/setMDPOpenState",
    async (
        {
            group_name,
            ticker_name,
            config_id,
            open_row,
            schedule_id,
        }: {
            group_name: string;
            ticker_name: string;
            config_id: number;
            open_row: string;
            schedule_id: string;
        },
        { rejectWithValue },
    ) => {
        const body = {
            group_name,
            ticker_name,
            config_id,
            open_rows: open_row ? [open_row] : [],
            schedule_id,
        };

        try {
            const response = await instance.post(
                `${pathname}/api/mdp-open-state`,
                body,
            );
            return { config_id, open_rows: response.data.open_rows };
        } catch (err: any) {
            notifyError(
                Object.values(err.response?.data || {})
                    .flat()
                    .join(" ") ||
                    err.message ||
                    "Произошла ошибка",
            );
            return rejectWithValue(
                err.response?.data || "Ошибка сохранения open state",
            );
        }
    },
);
export const getMDPSources = createAsyncThunk(
    "settings/getMDPSources",
    async (group_name: string, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                `${pathname}/api/mdp-sources?group_name=${group_name}`,
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Ошибка загрузки MDP источников",
            );
        }
    },
);

// Получение списка конфигов файлов
export const getConfigFilesList = createAsyncThunk(
    "settings/getConfigFilesList",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(`${pathname}/api/config/file`);
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data || e.message);
            }
            return rejectWithValue("Ошибка при загрузке списка конфигов");
        }
    },
);

export const getConfigFileDetails = createAsyncThunk(
    "settings/getConfigFileDetails",
    async (name: string, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                `${pathname}/api/config/file/${name}`,
            );
            return { name, data: response.data };
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.response?.data || e.message);
            }
            return rejectWithValue(`Ошибка при загрузке деталей для ${name}`);
        }
    },
);

export const initializeConfigData = createAsyncThunk(
    "settings/initializeConfigData",
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            await dispatch(getConfigFilesList()).unwrap();

            const state = getState() as RootState;
            const configNames = state.settings.configFilesList.map(
                (cf) => cf.name,
            );

            await Promise.all(
                configNames.map((name) =>
                    dispatch(getConfigFileDetails(name)).unwrap(),
                ),
            );

            return true;
        } catch (err) {
            notifyError(
                Object.values(err.response?.data || {})
                    .flat()
                    .join(" ") ||
                    err.message ||
                    "Произошла ошибка",
            );
            return rejectWithValue(err);
        }
    },
);

// заполнение новой мдп строки
export const getMDPPrefill = createAsyncThunk(
    "settings/getMDPPrefill",
    async (
        {
            ticker_name,
            group_name,
            workspace_id,
            schedule_id,
            index,
            robot_id,
        }: {
            ticker_name: string;
            group_name: string;
            workspace_id?: number;
            schedule_id?: string;
            index?: number | string;
            robot_id?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const params: any = {
                group_name,
                workspace_id,
                schedule_id,
                index,
                robot_id,
            };

            Object.keys(params).forEach((key) => {
                if (params[key] === undefined || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await instance.get(
                `${pathname}/api/ticker/${ticker_name}/mdp-prefill`,
                { params },
            );
            return response.data;
        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                notifyError(
                    Object.values(err.response?.data || {})
                        .flat()
                        .join(" ") ||
                        err.message ||
                        "Ошибка создания новой строки мдп MDP",
                );
                return rejectWithValue(err.response?.data);
            }
            return rejectWithValue("Ошибка при получении предзаполнения MDP");
        }
    },
);

export const getApplyFiles = createAsyncThunk(
    "get/config/file/apply",
    async (_, store) => {
        try {
            const response = await instance.get(
                pathname + "/api/config/file/apply",
            );
            return response.data;
        } catch (error) {
            console.error(error);
            notifyError("Не удалось загрузить список файлов");
            return;
        }
    },
);

const settingsSlice = createSlice({
    name: "SettingsSlice",
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        dropDraft(state) {
            state.saved_settings = state.saved_settings?.map((x) => {
                return {
                    ...x,
                    value: "",
                    has_change: false,
                };
            });

            state.isChanged = false;
        },
        resetSavedCPSettings(state) {
            state.isChanged = false;
        },
        dropState(state, action) {
            const { exclude_ticker } = action.payload;
            const ACTIVE_MDP = [];

            state.saved_settings = state.saved_settings.filter(
                (x) =>
                    x?.affiliation?.ticker_name !== exclude_ticker &&
                    ACTIVE_MDP.includes(x?.affiliation?.exchange_name),
            );

            const CP_STATE = current(state.saved_CP_settings);
            for (const key of Object.keys(CP_STATE)) {
                if (Array.isArray(CP_STATE[key])) {
                    state.saved_CP_settings[key] = CP_STATE[key].filter(
                        (x) => x?.affiliation?.ticker_name !== exclude_ticker,
                    );
                }
            }
        },
        setSelectedSCId(state, action) {
            state.selectedSCId = action.payload;
        },
        setSelectedSTSId(state, action) {
            state.selectedSTSId = [
                ...state.selectedSTSId.filter(
                    (sts) => sts.id !== action.payload.id,
                ),
                { id: action.payload.id, value: action.payload.value },
            ];
        },
        resetSelectedSTSId(state) {
            state.selectedSTSId = state.selectedSTSId.map((sts) => {
                return { id: sts.id, value: null };
            });
        },
        setAvailableSCParams(state, action) {
            state.availableSCIds = action.payload;
        },
        setAvailableSTSParams(state, action) {
            state.availableSTSIds = action.payload;
        },

        resetTicker(state, action) {
            const { ticker_name } = action.payload;
            state.saved_settings = state.saved_settings.filter(
                (x) =>
                    x.affiliation && x.affiliation.ticker_name !== ticker_name,
            );
            const CP_STATE = current(state.saved_CP_settings);
            const new_cp = {
                ...Object.keys(CP_STATE)?.map((key) => ({
                    [key]: CP_STATE[key].filter(
                        (x) =>
                            x.affiliation &&
                            x.affiliation.ticker_name !== ticker_name,
                    ),
                })),
            };
            //@ts-ignore
            state.saved_CP_settings = new_cp;
            state.MDPSelectedItem = initialState.MDPSelectedItem;
            state.activeTicker = initialState.activeTicker;

            state.isChanged = false;
            state.isNewTickerAdding = false;
            state.resetKey += 1;
        },
        resetConfigs(state) {
            const workspaceId = state.activeTabId;
            const snapshot =
                workspaceId === null
                    ? state.configs
                    : state.workspaceSnapshots[workspaceId] || state.configs;
            state.saved_configs = cloneConfigs(snapshot);
            state.configs = cloneConfigs(snapshot);
            if (workspaceId !== null) {
                state.workspaceDrafts[workspaceId] = cloneConfigs(snapshot);
                state.workspaceDirty[workspaceId] = false;
                state.workspaceValidationErrors[workspaceId] = {};
            }
            state.validationErrors = {};
            state.isTypeError = false;
            state.isChanged = false;
            state.sharedConfigValues = {};
            state.sharedTableValues = {};
            state.tickerDraftDirty = false;
            Object.keys(state.workspaceSnapshots).forEach((key) => {
                const workspaceId = Number(key);
                state.workspaceDrafts[workspaceId] = cloneConfigs(
                    state.workspaceSnapshots[workspaceId],
                );
                state.workspaceDirty[workspaceId] = false;
            });
            state.resetConfigsCounter += 1;
        },
        incrementResetConfigsCounter(state) {
            state.resetConfigsCounter += 1;
        },

        resetState: () => initialState,

        resetTickerSettings(state) {
            state.MDPSelectedItem = initialState.MDPSelectedItem;
            // state.searchString = ""
            state.activeTicker = initialState.activeTicker;

            state.isChanged = false;
            state.isNewTickerAdding = false;
            state.resetKey += 1;
        },
        resetGroup(state) {
            state.activeGroup = initialState.activeGroup;
            state.MDPSelectedItem = initialState.MDPSelectedItem;
            // state.searchString = ""
            state.activeTicker = initialState.activeTicker;

            state.isChanged = false;
            state.isNewTickerAdding = false;
            state.resetKey += 1;
        },

        setIsReset(state, action) {
            state.isReset = action.payload;
            state.isChanged = false;
        },
        setMDPSelectedItem(state, action) {
            state.MDPSelectedItem = action.payload;
        },
        setSelectedGroup(state, action) {
            const isSameGroup =
                state.activeGroup.name === action.payload.name &&
                state.activeGroup.id === action.payload.id;
            state.activeGroup = action.payload;
            state.MDPSelectedItem = initialState.MDPSelectedItem;
            if (isSameGroup) return;
            state.workspaceDrafts = {};
            state.workspaceSnapshots = {};
            state.workspaceDirty = {};
            state.workspaceValidationErrors = {};
            state.validationErrors = {};
            state.isTypeError = false;
            state.sharedConfigValues = {};
            state.sharedTableValues = {};
            state.tickerDraftDirty = false;
        },
        setSelectedTicker(state, action) {
            const nextScheduleId =
                action.payload.schedule_id !== undefined
                    ? action.payload.schedule_id
                    : state.activeTicker.schedule_id;
            const nextIndex =
                action.payload.index !== undefined
                    ? action.payload.index
                    : state.activeTicker.index;
            const isSameTicker =
                state.activeTicker.name === action.payload.name &&
                state.activeTicker.schedule_id === nextScheduleId &&
                state.activeTicker.index === nextIndex &&
                state.activeTicker.isNew === !!action.payload.isNew;
            state.activeTicker.name = action.payload.name;
            state.activeTicker.isNew = !!action.payload.isNew;
            if (action.payload.schedule_id !== undefined) {
                state.activeTicker.schedule_id = action.payload.schedule_id;
            }
            if (action.payload.index !== undefined) {
                state.activeTicker.index = action.payload.index;
            }

            state.MDPSelectedItem = initialState.MDPSelectedItem;
            if (isSameTicker) return;
            state.changedCPColumns = [];
            state.changedMDPColumns = [];
            if (!action.payload.isNew) {
                state.loading = true;
            }
            state.saved_configs = [];
            state.configs = [];
            state.isChanged = false;
            state.workspaceDrafts = {};
            state.workspaceSnapshots = {};
            state.workspaceDirty = {};
            state.workspaceValidationErrors = {};
            state.validationErrors = {};
            state.isTypeError = false;
            state.sharedConfigValues = {};
            state.sharedTableValues = {};
            state.tickerDraftDirty = false;
        },
        resetSelectedTicker(state) {
            state.activeTicker.name = "Название тикера";
            state.MDPSelectedItem = initialState.MDPSelectedItem;
            state.saved_configs = [];
            state.configs = [];
            state.isChanged = false;
            state.workspaceDrafts = {};
            state.workspaceSnapshots = {};
            state.workspaceDirty = {};
            state.workspaceValidationErrors = {};
            state.validationErrors = {};
            state.isTypeError = false;
            state.sharedConfigValues = {};
            state.sharedTableValues = {};
            state.tickerDraftDirty = false;
        },
        setIsTypeError(state, action) {
            state.isTypeError = action.payload;
            if (!action.payload) state.validationErrors = {};
        },
        setConfigValidationError: (
            state,
            action: PayloadAction<{
                configId: number;
                fieldKey: string;
                hasError: boolean;
            }>,
        ) => {
            const { configId, fieldKey, hasError } = action.payload;
            const key = `${configId}:${fieldKey}`;
            if (hasError) state.validationErrors[key] = true;
            else delete state.validationErrors[key];
            state.isTypeError = Object.keys(state.validationErrors).length > 0;
            if (state.activeTabId !== null) {
                state.workspaceValidationErrors[state.activeTabId] = {
                    ...state.validationErrors,
                };
            }
        },
        addDisplayedCommonParam: (state, action) => {
            const { configId, paramKey } = action.payload;
            const config = state.saved_configs.find((c) => c.id === configId);
            if (!config || config.config_code === "MDP") return;

            const keys = config.values[0];
            const values = config.values[1];

            const index = keys.indexOf(paramKey);
            if (index === -1) return;

            if (values[index] === "") {
                return;
            }
        },
        removeDisplayedCommonParam: (
            state,
            action: PayloadAction<{ configId: number; paramKey: string }>,
        ) => {
            const { configId, paramKey } = action.payload;
            const config = state.saved_configs.find((c) => c.id === configId);
            if (!config || config.config_code === "MDP") return;

            const keys = config.values[0];
            const values = config.values[1];

            const index = keys.indexOf(paramKey);
            if (index !== -1) {
                config.values[1] = [
                    ...values.slice(0, index),
                    "",
                    ...values.slice(index + 1),
                ];
                keys[index] = "";
                values[index] = "";
                config.values = [keys, values];
                state.isChanged = true;
                if (state.activeTabId !== null) {
                    state.workspaceDirty[state.activeTabId] = true;
                    cacheActiveWorkspaceDraft(state);
                }
            }
        },
        resetMDPOpenStates: (state) => {
            state.mdpOpenStates = [];
        },
        resetChangedColumns: (state) => {
            state.changedCPColumns = [];
            state.changedMDPColumns = [];
        },
        updateConfigValue: (
            state,
            action: PayloadAction<{
                configId: number;
                paramKey: string;
                newValue: string;
            }>,
        ) => {
            const { configId, paramKey, newValue } = action.payload;
            const config = state.saved_configs.find((c) => c.id === configId);

            if (!config) return;

            const metadata = state.configFilesDetails?.[config.config_code];
            if (metadata?.is_uniq_ui) return;

            const currentKeys = Array.isArray(config.values?.[0])
                ? config.values[0]
                : [];
            const currentValues = Array.isArray(config.values?.[1])
                ? config.values[1]
                : [];

            if (config.config_code === "CP") {
                const currentIndex = currentKeys.findIndex(
                    (key) => key?.trim() === paramKey.trim(),
                );
                if (currentValues[currentIndex] !== newValue) {
                    state.changedCPColumns = Array.from(
                        new Set([...state.changedCPColumns, paramKey.trim()]),
                    );
                }
            }

            const newKeys = [...currentKeys];
            const newValues = [...currentValues];

            let found = false;

            // for (let i = 0; i < newKeys.length; i++) {
            //     if (newKeys[i] && newKeys[i].trim() === paramKey.trim()) {
            //         if (
            //             paramKey.trim() === "HedgeInstId" &&
            //             state.activeTicker.isNew
            //         ) {
            //             newValues[i] = "";
            //         } else {
            //             newValues[i] = newValue;
            //         }
            //         found = true;
            //         break;
            //     }
            // }
            for (let i = 0; i < newKeys.length; i++) {
                if (newKeys[i] && newKeys[i].trim() === paramKey.trim()) {
                    newValues[i] = newValue;

                    found = true;
                    break;
                }
            }

            if (!found) {
                newKeys.push(paramKey);
                newValues.push(newValue);
            }

            config.values = [newKeys, newValues];
            state.sharedConfigValues[config.config_code] = {
                ...(state.sharedConfigValues[config.config_code] || {}),
                [paramKey]: newValue,
            };
            state.tickerDraftDirty = true;
            state.isChanged = true;
            if (state.activeTabId !== null) {
                state.workspaceDirty[state.activeTabId] = true;
                cacheActiveWorkspaceDraft(state);
            }
        },
        setActiveTabId: (state, action: PayloadAction<number | null>) => {
            cacheActiveWorkspaceDraft(state);
            state.activeTabId = action.payload;
            if (action.payload === null) return;

            const draft = state.workspaceDrafts[action.payload];
            if (draft) {
                state.saved_configs = applySharedValues(state, draft);
                state.configs = cloneConfigs(
                    state.workspaceSnapshots[action.payload] || draft,
                );
                state.isChanged =
                    state.tickerDraftDirty ||
                    !!state.workspaceDirty[action.payload];
                state.loading = false;
            } else {
                state.saved_configs = [];
                state.configs = [];
                state.isChanged = false;
                state.loading =
                    !!state.activeTicker.name &&
                    state.activeTicker.name !== "Название тикера";
            }
            state.validationErrors = {
                ...(state.workspaceValidationErrors[action.payload] || {}),
            };
            state.isTypeError = Object.keys(state.validationErrors).length > 0;
        },

        updateMDPRow: (
            state,
            action: PayloadAction<{
                configId: number;
                rowIndex: number;
                newRow: string[];
            }>,
        ) => {
            const { configId, rowIndex, newRow } = action.payload;

            const srcConfig = state.saved_configs.find(
                (c) => c.id === configId && c.config_code === "MDP",
            );
            if (!srcConfig || !srcConfig.all_values) return;

            const header = srcConfig.all_values[0] || [];
            const previousRow = srcConfig.all_values[rowIndex + 1] || [];
            newRow.forEach((value, index) => {
                const column = header[index];
                if (column && previousRow[index] !== value) {
                    state.changedMDPColumns = Array.from(
                        new Set([...state.changedMDPColumns, column]),
                    );
                }
            });

            srcConfig.all_values[rowIndex + 1] = [...newRow];
            state.isChanged = true;

            // Синхронизируем весь конфиг во все остальные MDP-конфиги
            state.saved_configs.forEach((cfg) => {
                if (cfg.config_code !== "MDP" || cfg.id === configId) return;

                // Полностью заменяем all_values на копию из текущего
                cfg.all_values = srcConfig.all_values.map((row) => [...row]);
            });
            state.sharedTableValues[srcConfig.config_code] =
                srcConfig.all_values.map((row) => [...row]);
            state.tickerDraftDirty = true;
            if (state.activeTabId !== null) {
                state.workspaceDirty[state.activeTabId] = true;
                cacheActiveWorkspaceDraft(state);
            }
        },

        addMDPRow: (
            state,
            action: PayloadAction<{ configId: number; newRow: string[] }>,
        ) => {
            const { configId, newRow } = action.payload;

            const config = state.saved_configs.find((c) => c.id === configId);
            if (config && config.config_code === "MDP") {
                config.all_values.push([...newRow]);
                state.sharedTableValues[config.config_code] =
                    config.all_values.map((row) => [...row]);
                state.tickerDraftDirty = true;
                state.isChanged = true;
                if (state.activeTabId !== null) {
                    state.workspaceDirty[state.activeTabId] = true;
                    cacheActiveWorkspaceDraft(state);
                }
            }
        },
        removeMDPRow: (
            state,
            action: PayloadAction<{ configId: number; rowIndex: number }>,
        ) => {
            const { configId, rowIndex } = action.payload;

            const srcConfig = state.saved_configs.find(
                (c) => c.id === configId && c.config_code === "MDP",
            );

            if (!srcConfig?.all_values) return;

            const allValuesIndex = rowIndex + 1;

            srcConfig.all_values.splice(allValuesIndex, 1);

            state.saved_configs.forEach((cfg) => {
                if (cfg.config_code !== "MDP" || cfg.id === configId) return;

                cfg.all_values = srcConfig.all_values.map((row) => [...row]);
            });

            state.sharedTableValues[srcConfig.config_code] =
                srcConfig.all_values.map((row) => [...row]);
            state.tickerDraftDirty = true;
            state.isChanged = true;
            if (state.activeTabId !== null) {
                state.workspaceDirty[state.activeTabId] = true;
                cacheActiveWorkspaceDraft(state);
            }
        },
        setAvailableTickers: (state, action) => {
            state.availableTickers = action.payload;
        },

        setShowDescriptions(state, action) {
            state.showDescriptions = action.payload;
        },
        setTickerBlocksSnapshot(
            state,
            action: PayloadAction<TickerBlockItem[]>,
        ) {
            state.tickerBlocks.items = action.payload;
            state.tickerBlocks.lastUpdate = new Date().toISOString();
        },

        addTickerBlock(state, action: PayloadAction<TickerBlockItem>) {
            const exists = state.tickerBlocks.items.some(
                (item) =>
                    item.group_name === action.payload.group_name &&
                    item.ticker_name === action.payload.ticker_name,
            );
            if (!exists) {
                state.tickerBlocks.items.push(action.payload);
                state.tickerBlocks.lastUpdate = new Date().toISOString();
                if (
                    action.payload.group_name === state.activeGroup.name &&
                    action.payload.ticker_name === state.activeTicker.name
                ) {
                    state.error = {
                        block: `Тикер заблокирован пользователем ${action.payload.blocked_by}`,
                    };
                }
            }
        },
        // settingsSlice.ts
        removeTickerBlock(
            state,
            action: PayloadAction<{ group_name: string; ticker_name: string }>,
        ) {
            const wasBlocked = state.tickerBlocks.items.some(
                (item) =>
                    item.group_name === action.payload.group_name &&
                    item.ticker_name === action.payload.ticker_name,
            );

            state.tickerBlocks.items = state.tickerBlocks.items.filter(
                (item) =>
                    !(
                        item.group_name === action.payload.group_name &&
                        item.ticker_name === action.payload.ticker_name
                    ),
            );
            state.tickerBlocks.lastUpdate = new Date().toISOString();

            if (
                wasBlocked &&
                action.payload.group_name === state.activeGroup.name &&
                action.payload.ticker_name === state.activeTicker.name
            ) {
                if (
                    (typeof state.error === "string" &&
                        state.error.includes("заблокирован")) ||
                    (typeof state.error === "object" && state.error?.block)
                ) {
                    state.error = null;
                }
            }
        },

        setTickerBlocksLoading(state, action: PayloadAction<boolean>) {
            state.tickerBlocks.loading = action.payload;
        },
        setEditMode(state, action: PayloadAction<boolean>) {
            state.editMode = action.payload;
        },
    },

    extraReducers: {
        [getMDP.fulfilled.type]: (state, action) => {
            const list = action.payload;

            const paramTypes: Record<string, string> = {};
            const defaultValues: Record<string, string | null> = {};
            const desc: Record<string, string | null> = {};
            list.forEach((x: any) => {
                paramTypes[x.name] = x.type;
                defaultValues[x.name] = x.default_value ?? null;
                desc[x.name] = x.description ?? null;
            });
        },

        [getMDPSourceTypes.fulfilled.type]: (state, action) => {
            state.MDPTypes = action.payload;
        },
        [getTickerSettings.pending.type]: (state) => {
            state.loading = true;
        },
        [getTickerSettings.rejected.type]: (state, action) => {
            state.loading = false;
            const error = action.payload;
            if (typeof error === "string") {
                state.error = "Ошибка!";
                return;
            }
            try {
                console.log("error", error);
                let errorMessage = "Неизвестная ошибка";
                if (error?.block?.[0]) {
                    const user = error.block[0].split("user ")[1];
                    if (user) {
                        errorMessage = `Тикер занят пользователем ${user}`;
                        state.error = errorMessage;
                    }
                } else if (error?.index?.[0]) {
                    errorMessage = error.index[0];
                }
            } catch (error) {
                console.error("catched error: ", error);
            }
        },
        [getTickerSettings.fulfilled.type]: (state, action) => {
            const { configs: apiConfigs, workspaceId } = action.payload as {
                configs: TickerConfig[];
                workspaceId: number | null;
            };
            console.log("getTickerSettings.fulfilled.type");

            if (workspaceId !== null && !state.workspaceDirty[workspaceId]) {
                state.workspaceSnapshots[workspaceId] =
                    cloneConfigs(apiConfigs);
                state.workspaceDrafts[workspaceId] = applySharedValues(
                    state,
                    apiConfigs,
                );
            }

            if (workspaceId !== state.activeTabId) return;

            state.loading = false;
            state.error = null;
            state.isNewTickerAdding = false;

            const configsToShow =
                workspaceId !== null && state.workspaceDirty[workspaceId]
                    ? state.workspaceDrafts[workspaceId]
                    : applySharedValues(state, apiConfigs);
            state.configs = cloneConfigs(
                workspaceId !== null
                    ? state.workspaceSnapshots[workspaceId] || apiConfigs
                    : apiConfigs,
            );
            state.saved_configs = cloneConfigs(configsToShow);
            const tickerMeta = apiConfigs as TickerConfig[] & {
                schedule_id?: string;
                index?: number;
            };
            if (tickerMeta.schedule_id || tickerMeta.index !== undefined) {
                state.activeTicker.schedule_id = tickerMeta.schedule_id || "";
                state.activeTicker.index = tickerMeta.index ?? -1;
            }
            if (state.activeTicker.isNew) {
                const cpDefaultValues =
                    state.configParams["CP"]?.defaultValues || {};
                state.saved_configs = state.saved_configs.map((config) => {
                    if (config.config_code !== "CP") return config;

                    let keys = [...(config.values?.[0] || [])];
                    let values = [...(config.values?.[1] || [])];

                    keys.forEach((key, idx) => {
                        if (key && key.trim() !== "") {
                            const trimmedKey = key.trim();
                            // ?
                            // if (trimmedKey === "HedgeInstId") {
                            //     values[idx] = "";
                            // } else
                            if (trimmedKey === "ID.depend") {
                                const tickerName =
                                    state.activeTicker?.name || "";
                                values[idx] = tickerName;
                            } else if (
                                !values[idx] ||
                                values[idx].trim() === ""
                            ) {
                                const defaultVal = cpDefaultValues[trimmedKey];
                                values[idx] =
                                    defaultVal != null && defaultVal !== ""
                                        ? String(defaultVal)
                                        : "0";
                            }
                        }
                    });

                    return {
                        ...config,
                        values: [keys, values],
                    };
                });
            }
            if (workspaceId !== null && !state.workspaceDirty[workspaceId]) {
                state.workspaceDrafts[workspaceId] = cloneConfigs(
                    state.saved_configs,
                );
            }
            state.isChanged =
                state.tickerDraftDirty ||
                (workspaceId !== null && !!state.workspaceDirty[workspaceId]);
        },
        [updateTickerSettings.fulfilled.type]: (state) => {
            state.isChanged = false;
            state.isNewTickerAdding = false;
        },
        [saveTickerSettingsNew.fulfilled.type]: (state) => {
            Object.keys(state.workspaceDrafts).forEach((key) => {
                const workspaceId = Number(key);
                const updatedConfigs = applySharedValues(
                    state,
                    state.workspaceDrafts[workspaceId],
                );
                state.workspaceDrafts[workspaceId] = updatedConfigs;
                state.workspaceSnapshots[workspaceId] =
                    cloneConfigs(updatedConfigs);
                state.workspaceDirty[workspaceId] = false;
            });
            if (state.activeTabId !== null) {
                state.workspaceDirty[state.activeTabId] = false;
                state.workspaceDrafts[state.activeTabId] = cloneConfigs(
                    state.saved_configs,
                );
                state.workspaceSnapshots[state.activeTabId] = cloneConfigs(
                    state.saved_configs,
                );
                state.workspaceValidationErrors[state.activeTabId] = {};
            }
            state.validationErrors = {};
            state.isTypeError = false;
            state.isChanged = false;
            state.isNewTickerAdding = false;
            state.sharedConfigValues = {};
            state.sharedTableValues = {};
            state.tickerDraftDirty = false;
        },
        // области
        [getTickerWorkspaces.pending.type]: (state) => {
            state.loading = true;
            state.error = null;
        },
        [getTickerWorkspaces.fulfilled.type]: (state, action) => {
            state.loading = false;
            state.workspaceTabs = action.payload;
            const activeTab = action.payload.find(
                (tab: WorkspaceTab) => tab.is_active,
            );
            state.activeTabId = activeTab ? activeTab.id : null;
        },
        [getTickerWorkspaces.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.payload || "Ошибка загрузки рабочих областей";
        },

        [createTickerWorkspace.fulfilled.type]: (state, action) => {
            state.workspaceTabs.push(action.payload);
        },

        [updateTickerWorkspace.fulfilled.type]: (state, action) => {
            const updatedTab = action.payload;
            const index = state.workspaceTabs.findIndex(
                (t) => t.id === updatedTab.id,
            );
            if (index !== -1) {
                state.workspaceTabs.forEach((t) => (t.is_active = false));
                state.workspaceTabs[index] = updatedTab;
            }
        },
        [deleteTickerWorkspace.fulfilled.type]: (state, action) => {
            const deletedId = action.payload;
            state.workspaceTabs = state.workspaceTabs.filter(
                (tab) => tab.id !== deletedId,
            );

            if (state.activeTabId === deletedId) {
                const firstTab = state.workspaceTabs[0];
                state.activeTabId = firstTab ? firstTab.id : null;
            }
        },
        [setMDPOpenState.fulfilled.type]: (state, action) => {
            const { config_id, open_rows } = action.payload;

            state.mdpOpenStates = [
                ...(state.mdpOpenStates || []).filter(
                    (item) => item.config_id !== config_id,
                ),
                {
                    config_id,
                    open_rows: open_rows || [],
                },
            ];

            state.openedMDPRows[config_id] = open_rows || [];
        },

        [getMDPOpenState.fulfilled.type]: (state, action) => {
            const { config_id, data } = action.payload;

            const record = data?.find(
                (item: any) => item.config_id === config_id,
            );

            if (record) {
                state.mdpOpenStates = [
                    ...(state.mdpOpenStates || []).filter(
                        (item) => item.config_id !== config_id,
                    ),
                    {
                        config_id: record.config_id,
                        open_rows: record.open_rows || [],
                        group_name: record.group_name,
                        ticker_name: record.ticker_name,
                        updated_at: record.updated_at,
                    },
                ];
            }
        },

        [getMDPSources.fulfilled.type]: (state, action) => {
            state.mdpSources = action.payload;
        },
        [getScheduleParams.fulfilled.type]: (state, action) => {
            if (action.payload) {
                state.availableSCIds = action.payload.sc_schedule_ids || [];
                state.availableSTSIds = action.payload.sts_schedule_ids || [];
                state.scSchedules = action.payload.sc_schedules || null;
                state.stsSchedules = action.payload.sts_schedules || null;
                state.sheduleExists = action.payload.shedule_exists || [];
            }
        },
        // новые
        [getConfigFilesList.pending.type]: (state) => {
            state.configFilesLoading = true;
            state.configFilesError = null;
        },
        [getConfigFilesList.fulfilled.type]: (state, action) => {
            state.configFilesLoading = false;
            state.configFilesList = action.payload;
        },
        [getConfigFilesList.rejected.type]: (state, action) => {
            state.configFilesLoading = false;
            state.configFilesError = action.payload as string;
        },

        [getConfigFileDetails.pending.type]: (state) => {
            state.configFilesLoading = true;
        },
        [getConfigFileDetails.fulfilled.type]: (state, action) => {
            state.configFilesLoading = false;
            const { name, data } = action.payload;

            state.configFilesDetails[name] = data;

            const fields = data.fields || [];
            const types: Record<string, SettingsParamType> = {};
            const defaultValues: Record<string, string | null> = {};
            const descriptions: Record<string, string | null> = {};

            fields.forEach((field) => {
                types[field.name] = field.field_type as SettingsParamType;
                defaultValues[field.name] = field.default_value ?? null;
                descriptions[field.name] = field.description ?? null;
            });

            state.configParams[name] = {
                types,
                defaultValues,
                descriptions,
            };
        },
        [getConfigFileDetails.rejected.type]: (state, action) => {
            state.configFilesLoading = false;
            state.configFilesError = action.payload as string;
        },
        [getApplyFiles.fulfilled.type]: (state, action) => {
            if (action.payload) {
                state.applyFiles = action.payload;
            }
        },
    },
});

export const {
    setMDPSelectedItem,
    setSelectedGroup,
    setSelectedTicker,
    setIsReset,
    resetTickerSettings,
    resetGroup,

    resetTicker,

    dropState,
    dropDraft,
    setLoading,
    resetState,
    resetSavedCPSettings,
    updateConfigValue,
    setActiveTabId,
    removeDisplayedCommonParam,
    addDisplayedCommonParam,
    updateMDPRow,
    addMDPRow,
    removeMDPRow,
    resetConfigs,

    incrementResetConfigsCounter,
    setIsTypeError,
    setConfigValidationError,
    setAvailableSCParams,
    setAvailableSTSParams,
    setSelectedSCId,
    setSelectedSTSId,
    resetSelectedSTSId,
    setAvailableTickers,
    resetSelectedTicker,
    resetMDPOpenStates,
    resetChangedColumns,
    setShowDescriptions,
    setTickerBlocksSnapshot,
    addTickerBlock,
    removeTickerBlock,
    setTickerBlocksLoading,
    setEditMode,
} = settingsSlice.actions;

export default settingsSlice.reducer;
